import React, { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Alert,
  Spinner,
} from "reactstrap";

//i18n
import { withTranslation } from "react-i18next";
// Redux
import { connect, useDispatch, useSelector } from "react-redux";
import { changeLayoutMode } from "../../../store/actions";
import { layoutModeTypes } from "../../../constants/layout";
import { persistThemeMode } from "../../../shared/utils/theme";
import { Link } from "react-router-dom";
import withRouter from "components/Common/withRouter";
import { useAuth } from "auth/useAuth";
import { CognitoIdentityProviderClient, ChangePasswordCommand } from "@aws-sdk/client-cognito-identity-provider";
import config from "../../../config/env";
import { meetsPasswordPolicy, PASSWORD_POLICY_TEXT } from "../../../shared/utils/password";

// users
// import user1 from "../../../assets/images/users/avatar-1.jpg";

const ProfileMenu = props => {
  // Declare a new state variable, which we'll call "menu"
  const [menu, setMenu] = useState(false);

  const [username, setusername] = useState("");
  const auth = useAuth();
  const dispatch = useDispatch();
  const layoutModeType = useSelector((state) => state.Layout?.layoutModeType);
  const isDark = layoutModeType === layoutModeTypes.DARK;
  const toggleTheme = () => {
    const next = isDark ? layoutModeTypes.LIGHT : layoutModeTypes.DARK;
    persistThemeMode(next);
    dispatch(changeLayoutMode(next));
  };

  // Change password modal state
  const [pwdModalOpen, setPwdModalOpen] = useState(false);
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdError, setPwdError] = useState(null);
  const [pwdSuccess, setPwdSuccess] = useState(null);
  const [showPwd, setShowPwd] = useState(false);

  const openPwdModal = () => {
    setCurrentPwd("");
    setNewPwd("");
    setConfirmPwd("");
    setPwdError(null);
    setPwdSuccess(null);
    setPwdModalOpen(true);
  };

  const handleChangePassword = async () => {
    if (!currentPwd || !newPwd || !confirmPwd) {
      setPwdError("Preencha todos os campos.");
      return;
    }
    if (newPwd !== confirmPwd) {
      setPwdError("A nova senha e a confirmação não coincidem.");
      return;
    }
    if (!meetsPasswordPolicy(newPwd)) {
      setPwdError(PASSWORD_POLICY_TEXT);
      return;
    }
    try {
      setPwdSaving(true);
      setPwdError(null);
      const client = new CognitoIdentityProviderClient({ region: config.COGNITO_REGION });
      await client.send(new ChangePasswordCommand({
        AccessToken: auth.user?.access_token,
        PreviousPassword: currentPwd,
        ProposedPassword: newPwd,
      }));
      setPwdSuccess("Senha alterada com sucesso.");
      setTimeout(() => setPwdModalOpen(false), 2000);
    } catch (err) {
      console.error("Error changing password:", err);
      if (err.name === "NotAuthorizedException") {
        setPwdError("Senha atual incorreta.");
      } else if (err.name === "InvalidPasswordException") {
        setPwdError("A nova senha não atende aos requisitos de segurança.");
      } else {
        setPwdError("Erro ao alterar senha. Tente novamente.");
      }
    } finally {
      setPwdSaving(false);
    }
  };

  useEffect(() => {
    // Get user info from Cognito
    if (auth.isAuthenticated && auth.user) {
      // Try to get given_name from user claims
      const givenName = auth.user.profile.given_name || auth.user.profile.name || auth.user.profile.email || "User";
      // console.log(auth);
      setusername(givenName);
    } else if (localStorage.getItem("authUser")) {
      if (process.env.REACT_APP_DEFAULTAUTH === "firebase") {
        const obj = JSON.parse(localStorage.getItem("authUser"));
        setusername(obj.displayName);
      } else if (
        process.env.REACT_APP_DEFAULTAUTH === "fake" ||
        process.env.REACT_APP_DEFAULTAUTH === "jwt"
      ) {
        const obj = JSON.parse(localStorage.getItem("authUser"));
        setusername(obj.username);
      }
    }
  }, [props.success, auth.isAuthenticated, auth.user]);

  return (
    <React.Fragment>
      <Dropdown
        isOpen={menu}
        toggle={() => setMenu(!menu)}
        className="d-inline-block"
      >
        <DropdownToggle
          className="btn header-item d-flex align-items-center"
          id="page-header-user-dropdown"
          tag="button"
          aria-label="Minha conta"
          title={username || "Minha conta"}
        >
          {/* Icon is always visible: on phones (< xl) it is the only way to
              reach "Perfil", "Alterar senha" and "Sair". */}
          <i className="bx bx-user-circle font-size-22 align-middle" aria-hidden="true" />
          <span className="d-none d-xl-inline-block ms-2 me-1">{username}</span>
          <i className="mdi mdi-chevron-down d-none d-xl-inline-block" aria-hidden="true" />
        </DropdownToggle>
        <DropdownMenu className="dropdown-menu-end">
          <DropdownItem tag={Link} to="/profile">
            <i className="bx bx-user font-size-16 align-middle me-1" />
            {props.t("Profile")}
          </DropdownItem>
          <DropdownItem onClick={openPwdModal}>
            <i className="bx bx-lock font-size-16 align-middle me-1" />
            {props.t("Alterar Senha")}
          </DropdownItem>
          <DropdownItem onClick={toggleTheme} toggle={false}>
            <i className={`bx ${isDark ? "bx-sun" : "bx-moon"} font-size-16 align-middle me-1`} />
            {isDark ? "Tema claro" : "Tema escuro"}
          </DropdownItem>
          {/* <DropdownItem tag="a" href="/user-profile">
            <i className="bx bx-cog font-size-16 align-middle me-1" />
            {props.t("Account Settings")}
          </DropdownItem> */}
          {/* <DropdownItem tag="a" href="/crypto-wallet">
            <i className="bx bx-wallet font-size-16 align-middle me-1" />
            {props.t("My Wallet")}
          </DropdownItem>
          <DropdownItem tag="a" href="#">
            <span className="badge bg-success float-end">11</span>
            <i className="bx bx-wrench font-size-16 align-middle me-1" />
            {props.t("Settings")}
          </DropdownItem> */}
          {/* <DropdownItem tag="a" href="auth-lock-screen">
            <i className="bx bx-lock-open font-size-16 align-middle me-1" />
            {props.t("Lock screen")}
          </DropdownItem> */}
          <div className="dropdown-divider" />
          <Link to="/logout" className="dropdown-item">
            <i className="bx bx-power-off font-size-16 align-middle me-1 text-danger" />
            <span>{props.t("Logout")}</span>
          </Link>
        </DropdownMenu>
      </Dropdown>

      <Modal isOpen={pwdModalOpen} toggle={() => setPwdModalOpen(false)}>
        <ModalHeader toggle={() => setPwdModalOpen(false)}>Alterar senha</ModalHeader>
        <ModalBody>
          {pwdError && <Alert color="danger">{pwdError}</Alert>}
          {pwdSuccess && <Alert color="success">{pwdSuccess}</Alert>}
          <form onSubmit={(e) => { e.preventDefault(); handleChangePassword(); }} autoComplete="on">
            <FormGroup>
              <Label htmlFor="cp-current">Senha atual</Label>
              <Input id="cp-current" type={showPwd ? "text" : "password"} autoComplete="current-password"
                value={currentPwd} onChange={(e) => setCurrentPwd(e.target.value)} disabled={pwdSaving || !!pwdSuccess} />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="cp-new">Nova senha</Label>
              <Input id="cp-new" type={showPwd ? "text" : "password"} autoComplete="new-password"
                value={newPwd} onChange={(e) => setNewPwd(e.target.value)} disabled={pwdSaving || !!pwdSuccess} />
              <div className="form-text">{PASSWORD_POLICY_TEXT}</div>
            </FormGroup>
            <FormGroup>
              <Label htmlFor="cp-confirm">Confirmar nova senha</Label>
              <Input id="cp-confirm" type={showPwd ? "text" : "password"} autoComplete="new-password"
                value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} disabled={pwdSaving || !!pwdSuccess} />
            </FormGroup>
            <div className="form-check">
              <Input id="cp-show" type="checkbox" className="form-check-input" checked={showPwd} onChange={(e) => setShowPwd(e.target.checked)} />
              <Label htmlFor="cp-show" className="form-check-label">Mostrar senhas</Label>
            </div>
            <button type="submit" hidden aria-hidden="true" />
          </form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setPwdModalOpen(false)} disabled={pwdSaving}>Cancelar</Button>
          <Button color="primary" onClick={handleChangePassword} disabled={pwdSaving || !!pwdSuccess}>
            {pwdSaving ? <Spinner size="sm" className="me-1" /> : null}
            Alterar
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

ProfileMenu.propTypes = {
  success: PropTypes.any,
  t: PropTypes.any
};

const mapStatetoProps = state => {
  const { error, success } = state.Profile;
  return { error, success };
};

export default withRouter(
  connect(mapStatetoProps, {})(withTranslation()(ProfileMenu))
);
